import { useState, useEffect, useRef } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useTheme } from "../../context/ThemeContext";

export default function TopBar({ pageTitle, planState, onAddAccount, onLogout, onAccountInfo, onExportReports, onSidebarToggle }) {
  const isMobile = useIsMobile();
  const { isDark, toggleTheme } = useTheme();
  const isTrial    = planState && planState.plan === "trial";
  const daysLeft   = isTrial ? planState.daysLeft : 0;
  const urgent     = isTrial && daysLeft <= 3;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header style={{
      padding: isMobile ? "12px 16px" : "20px 32px", borderBottom: "1px solid var(--border)",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      background: "var(--bg-main)", position: "sticky", top: 0, zIndex: 100,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        {/* Hamburger button (mobile only) */}
        {isMobile && (
          <button
            onClick={onSidebarToggle}
            style={{
              display: "flex",
              background: "none", border: "none", cursor: "pointer",
              color: "var(--text-primary)", fontSize: "20px", padding: "4px",
              alignItems: "center", justifyContent: "center",
            }}
            aria-label="Open menu"
          >☰</button>
        )}
        <div>
          <h1 style={{ fontSize: isMobile ? "15px" : "20px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "var(--text-primary)", letterSpacing: "-0.025em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: isMobile ? "160px" : "none" }}>
            {pageTitle}
          </h1>
          {!isMobile && (
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "3px" }}>
              {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "8px" : "12px" }}>
        {/* Export Reports — hidden on mobile */}
        {!isMobile && (
          <div
            onClick={onExportReports}
            style={{
              padding: "8px 16px", borderRadius: "20px",
              background: "var(--bg-card)", border: "1px solid var(--border-strong)",
              fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace",
              cursor: "pointer", transition: "border-color 0.2s, color 0.2s",
              display: "flex", alignItems: "center", gap: "6px",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#D4AF37"; e.currentTarget.style.color = "#D4AF37"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
          >
            <span style={{ fontSize: "13px" }}>↓</span> Reports
          </div>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={isDark ? "Switch to light theme" : "Switch to dark theme"}
          style={{
            width: isMobile ? "32px" : "36px",
            height: isMobile ? "32px" : "36px",
            borderRadius: "10px",
            background: "transparent",
            border: "1px solid var(--border-strong)",
            color: "#D4AF37",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "15px",
            flexShrink: 0,
            transition: "background 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--bg-hover)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          {isDark ? "☀" : "◑"}
        </button>

        {/* Add Account — icon-only on mobile */}
        <div
          onClick={onAddAccount}
          style={{
            padding: isMobile ? "8px 10px" : "8px 16px", borderRadius: "20px",
            background: "var(--bg-card)", border: "1px solid var(--border-strong)",
            fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace",
            cursor: "pointer", transition: "border-color 0.2s, color 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#D4AF37"; e.currentTarget.style.color = "#D4AF37"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
        >
          {isMobile ? "+" : "+ Add Account"}
        </div>

        {/* Avatar + Dropdown */}
        <div ref={dropdownRef} style={{ position: "relative" }}>
          {/* Avatar — title="Log out" kept for E2E mount detection */}
          <div
            title="Log out"
            onClick={() => setDropdownOpen(prev => !prev)}
            style={{
              width: "36px", height: "36px", borderRadius: "50%",
              background: dropdownOpen
                ? "linear-gradient(135deg, #E8DDD0, #D4AF37)"
                : "linear-gradient(135deg, #D4AF37, #9A7C1E)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "14px", fontWeight: "600", color: "#121A12",
              cursor: "pointer", transition: "background 0.2s",
              boxShadow: dropdownOpen ? "0 0 0 2px #D4AF3740" : "none",
            }}
          >
            R
          </div>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 10px)", right: 0,
              minWidth: "220px", background: "var(--bg-card)",
              border: "1px solid var(--border-strong)", borderRadius: "12px",
              boxShadow: "0 16px 40px rgba(0,0,0,0.6)",
              overflow: "hidden", zIndex: 200,
              animation: "slideDown 0.15s ease",
            }}>
              <style>{`
                @keyframes slideDown {
                  from { opacity: 0; transform: translateY(-6px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
                .dd-item:hover { background: var(--bg-hover) !important; }
              `}</style>

              {/* User header */}
              <div style={{
                padding: "14px 16px", borderBottom: "1px solid var(--border-strong)",
                display: "flex", alignItems: "center", gap: "10px",
              }}>
                <div style={{
                  width: "32px", height: "32px", borderRadius: "50%",
                  background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "13px", fontWeight: "600", color: "#121A12", flexShrink: 0,
                }}>R</div>
                <div>
                  <div style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" }}>Revanth</div>
                  {isTrial ? (
                    <div style={{
                      fontSize: "10px", fontFamily: "'JetBrains Mono', monospace",
                      color: urgent ? "#e07070" : "#D4AF37",
                    }}>
                      {urgent ? `⏰ ${daysLeft}d left in trial` : `✦ Free Trial · ${daysLeft}d left`}
                    </div>
                  ) : (
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>Premium Plan</div>
                  )}
                </div>
              </div>

              {/* Account Settings */}
              <div
                className="dd-item"
                onClick={() => { setDropdownOpen(false); onAccountInfo(); }}
                style={{
                  padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px",
                  cursor: "pointer", transition: "background 0.15s",
                }}
              >
                <span style={{ fontSize: "15px" }}>⚙️</span>
                <span style={{ fontSize: "13px", color: "#D4AF37" }}>Account Settings</span>
              </div>

              {/* Divider */}
              <div style={{ height: "1px", background: "var(--bg-hover)", margin: "0 16px" }} />

              {/* Log Out */}
              <div
                className="dd-item"
                data-testid="logout-btn"
                onClick={() => { setDropdownOpen(false); onLogout(); }}
                style={{
                  padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px",
                  cursor: "pointer", transition: "background 0.15s",
                }}
              >
                <span style={{ fontSize: "15px" }}>🚪</span>
                <span style={{ fontSize: "13px", color: "#e07070" }}>Log Out</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
